import { P2GBox, P2GGrid, P2GLink, P2GText, P2GCheckBoxLegacy } from '@parcel2go/core-ui';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import BrSplitter from '../../../../lib/helpers/BrSplitter';
import {
  DefaultLinkGroup,
  GetLabelLinkGroupDescription,
  GetLinkLabelIcon,
  GetLinkLabelIconSrc
} from './GetLinkLabelIcon';
import { LinkModel } from './LinkModel';
import { DownloadContext } from '../../../../lib/context/DownloadContext';
import { uid } from '../../../../lib/helpers/uid';
import Base64Link from './Base64Link';
import { LinksProps } from './LinksProps';
import { EventSections } from '../../../../lib/events/EventLogger';
import { onlyUnique } from '../../../../lib/helpers/ArrayHelper';
import { AdditionalLinkInfo } from './AdditionalLinkInfo';
import { getLocalStorageValue, setLocalStorageValue } from '~/hooks/use-local-storage';
import PreSignedLink from './PreSigned';
import OrderLabelStatusObserver from '../OrderLabelStatusObserver';
import { useTranslation } from '@parcel2go/npm-p2g-app';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { useLinkTitle } from './useLinkTitle';
import { TranslationNamespaces } from '~/lib/constants';
import { sanitiseKey } from '~/lib/utils/translation';
import CdnIconProxy from '~/apps/account-linking/components/CdnIconProxy';
import { AppCard } from '~/components/card/app-card';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';

const LabelTypeConst = 'SmartSend.LabelLinkGroup';

const LabelLinkies = ({ l, tag, onClick }: { l: LinkModel; tag: string; onClick: () => void }) => {
  const { title } = useLinkTitle();

  if (l.Type.indexOf('BASE64') > -1) {
    return <Base64Link key="base-64" link={l} tag={tag} />;
  }

  if (l.Type.indexOf('PRESIGNED') > -1) {
    return <PreSignedLink key="presigned" link={l} tag={tag} onDownloaded={onClick} />;
  }

  return l.Disabled ? (
    <P2GLink key={l.Type} target={l.Target} variant="body2">
      <span style={{ opacity: 0.5 }}>
        <BrSplitter label={title(l)} />
      </span>
    </P2GLink>
  ) : (
    <P2GLink key={l.Type} onClick={onClick} href={l.Href} target={l.Target} variant="body2">
      <BrSplitter label={title(l)} />
    </P2GLink>
  );
};

const LabelAppCard = ({ l, tag }: { l: LinkModel; tag: string }) => {
  const dctx = useContext(DownloadContext);
  const { title } = useLinkTitle();
  const [completed, setCompleted] = useState(false);

  const addDownload = useCallback(
    (l: LinkModel) => {
      dctx.dispatch({
        type: 'ADD',
        payload: {
          Href: l.Href,
          Label: `${tag} \n ${title(l)} `,
          Status: 'Processing',
          Type: l.Type,
          UID: uid()
        }
      });
    },
    [dctx, tag, title]
  );

  const getDl = useCallback(
    (l: LinkModel) => {
      return (
        dctx.state.find((x) => x.Href === l.Href) || {
          Label: title(l),
          Href: undefined,
          Status: '',
          Type: 'Link'
        }
      );
    },
    [dctx.state, title]
  );

  const onClick = useCallback(() => {
    if (l.Type.includes('PRESIGNED')) {
      setCompleted(true);
    } else {
      addDownload(l);
    }
  }, [l, addDownload]);

  return (
    <AppCard variant={completed || getDl(l)?.Status === 'Ready' ? 'success' : undefined}>
      <P2GBox
        pt={2}
        pb={2}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'row', sm: 'column' },
          alignItems: 'center',
          justifyContent: 'center',
          gap: {
            xs: 2,
            sm: 0
          }
        }}
      >
        <P2GBox>
          <CdnIconProxy component="span" size="sm" palette="primary" src={GetLinkLabelIconSrc(l.Type)}>
            {GetLinkLabelIcon(l.Type)}
          </CdnIconProxy>
        </P2GBox>
        <P2GBox textAlign="center">
          <LabelLinkies l={{ ...l, Target: '_blank' }} onClick={onClick} tag={tag} />
        </P2GBox>
      </P2GBox>
    </AppCard>
  );
};

export const LabelLinks: React.FC<LinksProps & { isParent: boolean }> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { track } = useSmartSendAnalytics();
  const [preSignedLinks, setPreSignedLinks] = useState<LinkModel[]>([]);

  // Temp fix to remove the presigned links from the edge api and use the response from the status observer
  const links = useMemo(() => {
    if (preSignedLinks?.length) {
      return props.links.filter((x) => !x.Type.includes('PRESIGNED')).concat(preSignedLinks);
    }
    return props.links;
  }, [props.links, preSignedLinks]);

  const statusParams:
    | {
        statusEndpoint?: string;
        body?: {
          orderId?: string;
          orderLineId?: string;
          orderLineIds?: string[];
        };
      }
    | undefined = useMemo(() => {
    const output = links.find((x) => x.Type.endsWith('LABELS-STATUS'))?.Href;

    if (output) {
      // Get orderLineIds from the output and assign to the output model
      const params = new URLSearchParams(output.split('?')[1]);
      const orderId = params.get('orderId');
      const orderLineId = params.get('orderLineId');
      const orderLineIds = params.get('orderLineIds')?.split(',');
      return {
        statusEndpoint: output.split('?')[0],
        body: {
          orderId: orderId,
          orderLineId: orderLineId,
          orderLineIds: orderLineIds
        }
      };
    }

    return undefined;
  }, [links]);

  const linkGroups: string[] = useMemo(
    () =>
      props.variant != 'label'
        ? [DefaultLinkGroup]
        : links.map((x) => x.LinkGroup ?? DefaultLinkGroup).filter(onlyUnique),
    [links, props.variant]
  );

  const [selectedLinkGroup, setSelectedGroup] = useState<string>(linkGroups[0]);

  // Labels Only
  const filteredLabelLinks = useMemo(
    () =>
      links
        .filter((l) => (l.LinkGroup || DefaultLinkGroup) === selectedLinkGroup)
        .filter((x) => x.Type !== 'LABELS-STATUS' && x.Type !== 'ORDER-LABELS-STATUS'),
    [links, selectedLinkGroup]
  );

  // Retrieve from Cookie
  useEffect(() => {
    if (props.variant == 'label' && linkGroups.length > 1) {
      const option = getLocalStorageValue(LabelTypeConst);
      if (option != selectedLinkGroup && linkGroups.includes(option as string)) {
        setSelectedGroup(option as string);
      }
    }
  }, [linkGroups, props.variant, selectedLinkGroup]);

  const onLinkGroupChange = useCallback(
    (lg: string) => {
      {
        if (lg === DefaultLinkGroup) {
          track({
            type: 'label_option_changed',
            payload: GenerateBaseAnalyticsPayload([EventSections.SmartSend, EventSections.Book, 'labels', 'default'], {
              surface: 'booking-label-options',
              action: 'label-group-change',
              labelMode: 'default'
            })
          });
        } else {
          track({
            type: 'label_option_changed',
            payload: GenerateBaseAnalyticsPayload(
              [EventSections.SmartSend, EventSections.Book, 'labels', 'include-packing-slip'],
              {
                surface: 'booking-label-options',
                action: 'label-group-change',
                labelMode: 'include-packing-slip'
              }
            )
          });
        }
        const updatedValue = selectedLinkGroup === lg ? DefaultLinkGroup : lg;
        setSelectedGroup(updatedValue);
        setLocalStorageValue(LabelTypeConst, updatedValue);
      }
    },
    [selectedLinkGroup, track]
  );

  return (
    <OrderLabelStatusObserver
      isParent={props.isParent}
      statusParams={statusParams}
      setPreSignedLinks={setPreSignedLinks}
    >
      <P2GGrid container spacing={2} alignItems="stretch">
        {filteredLabelLinks.map((l) => (
          <P2GGrid item key={l.Type} sm={4} xs={12}>
            <LabelAppCard l={l} tag={props.tag} />
          </P2GGrid>
        ))}
      </P2GGrid>
      {linkGroups
        ?.filter((x) => x != DefaultLinkGroup)
        .map((lg) => (
          <P2GBox key={lg} mt={1}>
            <AppCard>
              <P2GBox p={1} pl={2}>
                <P2GCheckBoxLegacy
                  checked={selectedLinkGroup === lg}
                  onChange={() => onLinkGroupChange(lg)}
                  id={lg}
                  label={
                    <P2GText variant="body2">
                      {t(sanitiseKey(`LABELS_LINK_GROUP_${lg}_LABEL`), GetLabelLinkGroupDescription(lg))}
                    </P2GText>
                  }
                  error={false}
                  labelPlacement="end"
                  variant="primary"
                />
              </P2GBox>
            </AppCard>
          </P2GBox>
        ))}
      <AdditionalLinkInfo links={filteredLabelLinks} />
    </OrderLabelStatusObserver>
  );
};
